import { motion } from "framer-motion";
import { Check, X, Minus, Sparkles } from "lucide-react";
import Reveal from "@/components/landing/Reveal";
import { renderTitle, toFullUrl } from "@/components/landing/utils";

export const COMPARISON_DEFAULTS = {
  eyebrow: "Comparador · Esfuerzo, estructura y rendimiento",
  hook: "Tu capital puede participar en un negocio sin obligarte a construirlo <em>desde cero</em>.",
  title: "¿Conservar, operarlo tú, o crecer junto a una empresa que ya <em>sabe ejecutar</em>?",
  subhook: "Compara qué te exige cada opción, qué estructura respalda tu dinero y qué potencial tiene.",
  closing: "No inviertes en una plataforma. Inviertes en una expansión que puedes ver.",
  columns: [
    { name: "CETES / Renta fija", subtitle: "Pasivo, previsible, limitado", rendimiento: "Tasa de referencia fija · sin upside", rendimiento_score: 2, esfuerzo_score: 1, tiempo: "0 hrs", involucramiento: "Nulo · 100 % pasivo", estructura: "Instrumento financiero: no participas en ningún negocio", upside: "Sin participación en el crecimiento comercial", propio: "no", operas: "no", estructura_ok: "neutral" },
    { name: "Negocio propio o franquicia", subtitle: "Control total, carga operativa total", rendimiento: "Depende 100 % de tu ejecución", rendimiento_score: 3, esfuerzo_score: 5, tiempo: "40+ hrs / semana", involucramiento: "Local, personal, inventario, permisos y cobranza", estructura: "La construyes desde cero, con curva de aprendizaje", upside: "Limitado a tu unidad", propio: "yes", operas: "yes", estructura_ok: "no" },
    { name: "Proyecto Diamante", subtitle: "Participación en una expansión retail que ya opera", badge: "Mejor balance rendimiento / esfuerzo", rendimiento: "Vinculado a la utilidad de nuevas sucursales Rubí", rendimiento_score: 5, esfuerzo_score: 1, tiempo: "0 hrs operativas · reportes periódicos", involucramiento: "Estratégico: recibes reportes y distribuciones", estructura: "Marca, CEDIS, procesos, equipo y red de sucursales ya operando", upside: "Acompañas nuevas aperturas de una red en crecimiento", highlighted: true, propio: "yes", operas: "no", estructura_ok: "yes" },
  ],
};

const ROWS = [
  { key: "rendimiento", label: "Rendimiento", score: "rendimiento_score" },
  { key: "esfuerzo", label: "Esfuerzo requerido", score: "esfuerzo_score", invert: true },
  { key: "tiempo", label: "Tiempo" },
  { key: "involucramiento", label: "Involucramiento" },
  { key: "estructura", label: "Estructura que te respalda" },
  { key: "upside", label: "Upside" },
];

function ScoreBar({ value = 0, hi, invert }) {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  const good = invert ? v <= 2 : v >= 4;
  return (
    <div className="flex gap-1 mt-2" aria-label={`${v} de 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <motion.span
          key={n}
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.35, delay: n * 0.06 }}
          className={`h-1.5 flex-1 rounded-full origin-left ${n <= v ? (good ? "bg-[#FFBF57]" : hi ? "bg-white/70" : "bg-[#0A0A0A]/70") : hi ? "bg-white/10" : "bg-black/8"}`}
        />
      ))}
    </div>
  );
}

function Flag({ v, hi }) {
  if (v === "yes") return <Check size={13} className="text-[#FFBF57]" strokeWidth={2.5} />;
  if (v === "no") return <X size={13} className={hi ? "text-white/50" : "text-[#A1A1AA]"} strokeWidth={2.5} />;
  return <Minus size={13} className={hi ? "text-white/40" : "text-[#A1A1AA]"} />;
}

export default function ComparisonSection({ block, locations, simulator }) {
  const d = COMPARISON_DEFAULTS;
  const cols = (block?.columns?.length ? block.columns : d.columns).map((c, i) => ({ ...d.columns[i], ...c }));
  const photo = toFullUrl(block?.image_url) || toFullUrl((locations?.items || []).find((l) => l.image_url)?.image_url);
  const rateObj = simulator?.rate_objetivo ? Math.round(simulator.rate_objetivo * 100) : null;
  const ordered = cols.map((c, i) => ({ ...c, i })).sort((a, b) => (a.highlighted ? -1 : 0) - (b.highlighted ? -1 : 0));

  return (
    <section id="comparacion" className="bg-[#F4F4F5] py-20 md:py-32 relative overflow-hidden" data-testid="comparison-section">
      <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-end mb-12 md:mb-16">
          <div>
            <Reveal><span className="eyebrow">{block?.eyebrow || d.eyebrow}</span></Reveal>
            <Reveal delay={120}>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.02] text-[#0A0A0A]">{renderTitle(block?.hook || d.hook)}</h2>
            </Reveal>
          </div>
          <Reveal delay={220}>
            <p className="text-[#3F3F46] text-base md:text-lg leading-relaxed lg:pb-2">{block?.subhook || d.subhook}</p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 lg:gap-6 items-stretch">
          {ordered.map((c, idx) => {
            const hi = !!c.highlighted;
            return (
              <motion.div
                key={c.i}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: idx * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className={`relative rounded-3xl overflow-hidden flex flex-col ${hi ? "bg-[#0A0A0A] text-white border-2 border-[#FFBF57] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)] lg:-translate-y-4 lg:order-3 order-first" : "bg-white border border-black/8"} ${!hi && c.i === 0 ? "lg:order-1" : !hi ? "lg:order-2" : ""}`}
                data-testid={`comparison-col-${c.i}`}
              >
                {hi && (
                  <div className="relative h-36 md:h-44 overflow-hidden">
                    {photo && <img src={photo} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-[#0A0A0A]/20" />
                    <div className="absolute top-4 left-4 inline-flex items-center gap-2 text-[9px] uppercase tracking-[0.2em] font-mono bg-[#FFBF57] text-[#0A0A0A] px-3 py-1.5 rounded-full">
                      <Sparkles size={10} /> {c.badge || "Recomendado"}
                    </div>
                  </div>
                )}
                <div className="p-6 md:p-8 flex-1 flex flex-col">
                  <div className={`text-[10px] uppercase tracking-widest font-mono ${hi ? "text-[#FFBF57]" : "text-[#A1A1AA]"}`}>Opción {c.i + 1}</div>
                  <div className={`mt-2 font-display text-2xl md:text-[28px] leading-tight ${hi ? "text-white" : "text-[#0A0A0A]"}`}>{c.name}</div>
                  {c.subtitle && <div className={`mt-1.5 text-sm ${hi ? "text-[#A1A1AA]" : "text-[#52525B]"}`}>{c.subtitle}</div>}

                  <div className={`mt-6 grid grid-cols-3 gap-2 text-[10px] font-mono uppercase tracking-wider ${hi ? "text-white/80" : "text-[#52525B]"}`}>
                    <div className={`rounded-xl px-2.5 py-2 flex items-center gap-1.5 ${hi ? "bg-white/5" : "bg-[#FAFAFA] border border-black/5"}`}><Flag v={c.propio} hi={hi} /> Negocio real</div>
                    <div className={`rounded-xl px-2.5 py-2 flex items-center gap-1.5 ${hi ? "bg-white/5" : "bg-[#FAFAFA] border border-black/5"}`}><Flag v={c.operas === "yes" ? "no" : "yes"} hi={hi} /> Sin operar</div>
                    <div className={`rounded-xl px-2.5 py-2 flex items-center gap-1.5 ${hi ? "bg-white/5" : "bg-[#FAFAFA] border border-black/5"}`}><Flag v={c.estructura_ok} hi={hi} /> Estructura</div>
                  </div>

                  <div className={`mt-6 divide-y ${hi ? "divide-white/10" : "divide-black/6"}`}>
                    {ROWS.map((r) => (
                      <div key={r.key} className="py-4 first:pt-0 last:pb-0">
                        <div className={`text-[10px] uppercase tracking-widest font-mono ${hi ? "text-[#FFBF57]/80" : "text-[#A1A1AA]"}`}>{r.label}</div>
                        <div className={`mt-1 text-sm leading-snug ${hi ? "text-white" : "text-[#0A0A0A]"}`}>
                          {c[r.key]}
                          {hi && r.key === "rendimiento" && rateObj && <span className="ml-1.5 text-[#FFBF57] num-mono">· escenario objetivo {rateObj} %*</span>}
                        </div>
                        {r.score && <ScoreBar value={c[r.score]} hi={hi} invert={r.invert} />}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <Reveal delay={150}>
          <div className="mt-12 md:mt-16 grid md:grid-cols-[1fr_auto] gap-6 items-center rounded-3xl bg-[#0A0A0A] on-onyx text-white p-7 md:p-10 relative overflow-hidden">
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#FFBF57]/15 blur-3xl" />
            <div className="relative font-display text-2xl md:text-4xl leading-tight max-w-3xl">{renderTitle(block?.closing || d.closing)}</div>
            <a href="#proyectos" className="relative btn-primary !bg-[#FFBF57] !text-[#0A0A0A] justify-center" data-testid="comparison-cta">Ver Proyecto Diamante</a>
          </div>
        </Reveal>
        {(block?.disclaimer || rateObj) && (
          <p className="mt-6 text-[11px] text-[#71717A] leading-relaxed max-w-4xl">
            {rateObj ? "*Escenario objetivo del simulador; no constituye garantía. " : ""}{block?.disclaimer}
          </p>
        )}
      </div>
    </section>
  );
}
